import { type ReactNode } from 'react';
import { QueryProvider } from './QueryProvider';
import { AuthProvider } from '../contexts/AuthContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { PreferencesProvider } from '../contexts/PreferencesContext';
import { AppStateProvider } from '../contexts/AppStateContext';
import { I18nProvider } from '../contexts/I18nContext';
import { ThemeProvider } from '../contexts/ThemeContext';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Combined providers component that wraps the entire app with all necessary context providers.
 * The order of providers is important:
 * 1. QueryProvider - Provides TanStack Query client
 * 2. ThemeProvider - Provides theme management (needs to be early for CSS variables)
 * 3. I18nProvider - Provides internationalization
 * 4. AppStateProvider - Provides global app state
 * 5. PreferencesProvider - Provides user preferences
 * 6. NotificationProvider - Provides notification system
 * 7. AuthProvider - Provides authentication (depends on notifications)
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryProvider>
      <ThemeProvider>
        <I18nProvider>
          <AppStateProvider>
            <PreferencesProvider>
              <NotificationProvider>
                <AuthProvider>
                  {children}
                </AuthProvider>
              </NotificationProvider>
            </PreferencesProvider>
          </AppStateProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryProvider>
  );
}

export default AppProviders;